import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, Mic, MicOff, Sparkles, Zap, Brain, Clock,
  Bookmark, Share2, ChevronRight, ExternalLink, RefreshCw,
  X, History, Trash2, TrendingUp, Loader2, Send, Plus,
  Globe, FileText, Users, Image as ImageIcon, Settings2,
  MessageSquare, ArrowRight, Camera, Upload, ShoppingBag, Tag
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MagazineBackground from '../../components/search/MagazineBackground';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Search Mode Icons and Descriptions
const SEARCH_MODES = [
  { id: 'quick', label: 'Quick', icon: Zap, desc: 'Fast answers', gradient: 'from-blue-500 to-cyan-500' },
  { id: 'deep', label: 'Deep', icon: Brain, desc: 'Thorough analysis', gradient: 'from-purple-500 to-pink-500' },
  { id: 'research', label: 'Research', icon: Sparkles, desc: 'Multiple perspectives', gradient: 'from-amber-500 to-orange-500' },
];

// Source Type Icons
const SOURCE_ICONS = {
  'aira-echo': MessageSquare,
  'news': FileText,
  'default': Globe,
};

// Source Card Component - moved outside to prevent re-creation on every render
const SourceCard = ({ source, index }) => {
  const Icon = SOURCE_ICONS[source.domain] || SOURCE_ICONS.default;
  
  return (
    <motion.a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300"
      data-testid={`source-card-${index}`}
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center">
        <span className="text-xs font-bold text-purple-400">[{index + 1}]</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-400 truncate">{source.domain}</span>
        </div>
        <h4 className="text-sm font-medium text-white group-hover:text-purple-300 transition-colors line-clamp-2">
          {source.title}
        </h4>
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{source.snippet}</p>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-purple-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.a>
  );
};

export default function AiraSearchPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [searchMode, setSearchMode] = useState('quick');
  const [isSearching, setIsSearching] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [streamingAnswer, setStreamingAnswer] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  
  // Image Search State
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageSearchResult, setImageSearchResult] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [imageQuery, setImageQuery] = useState('');
  
  const inputRef = useRef(null);
  const answerRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Fetch initial data
  useEffect(() => {
    if (token) {
      fetchHistory();
      fetchSavedSearches();
    }
    fetchTrending();
    
    // Auto-search if query param exists
    if (searchParams.get('q')) {
      handleSearch(searchParams.get('q'));
    }
  }, [token]);
  
  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/search/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchHistory(data.history || []);
      }
    } catch (e) { console.error(e); }
  };
  
  const fetchTrending = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/search/trending?limit=6`);
      if (res.ok) {
        const data = await res.json();
        setTrendingSearches(data.trending || []);
      }
    } catch (e) { console.error(e); }
  };
  
  const fetchSavedSearches = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/search/saved?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedSearches(data.saved || []);
      }
    } catch (e) { console.error(e); }
  };
  
  // Main Search Handler with Streaming
  const handleSearch = async (searchQuery = query, followUp = false) => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setStreamingAnswer('');
    setSearchResult(null);
    setStatusMessage('Searching the web...');
    
    try {
      const endpoint = followUp ? '/api/search/follow-up' : '/api/search/stream';
      const body = followUp 
        ? { query: searchQuery, conversation_id: conversationId }
        : { query: searchQuery, search_mode: searchMode, conversation_id: conversationId };
      
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) throw new Error('Search failed');
      
      // Handle streaming response
      if (!followUp) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let sources = [];
        let relatedQuestions = [];
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'conversation') {
                  setConversationId(data.conversation_id);
                } else if (data.type === 'status') {
                  setStatusMessage(data.message);
                } else if (data.type === 'sources') {
                  sources = data.sources || [];
                  setSearchResult(prev => ({ ...prev, sources }));
                } else if (data.type === 'answer_chunk') {
                  setStreamingAnswer(prev => prev + data.chunk);
                } else if (data.type === 'done') {
                  relatedQuestions = data.related_questions || [];
                  setSearchResult(prev => ({
                    ...prev,
                    sources,
                    related_questions: relatedQuestions,
                    conversation_id: data.conversation_id
                  }));
                  setStatusMessage('');
                } else if (data.type === 'error') {
                  toast.error(data.error);
                }
              } catch (e) { /* ignore parse errors */ }
            }
          }
        }
      } else {
        // Non-streaming for follow-ups
        const data = await res.json();
        setSearchResult(data);
        setStreamingAnswer(data.ai_answer?.content || '');
      }
      
      // Update history
      fetchHistory();
      
    } catch (error) {
      toast.error('Search failed. Please try again.');
      console.error(error);
    } finally {
      setIsSearching(false);
      setStatusMessage('');
    }
  };
  
  // Voice Search
  const startVoiceSearch = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice search is not supported in your browser');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setQuery(transcript);
      
      if (event.results[0].isFinal) {
        setTimeout(() => handleSearch(transcript), 500);
      }
    };
    
    recognition.onerror = () => {
      setIsListening(false);
      toast.error('Voice recognition failed');
    };
    
    recognition.start();
  }, []);
  
  const stopVoiceSearch = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, []);
  
  // Image Search Functions
  const handleImageSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    
    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    setShowImageSearch(true);
  }, []);
  
  const handleImageDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = { target: { files: [file] } };
      handleImageSelect(fakeEvent);
    }
  }, [handleImageSelect]);
  
  const handleImageSearch = async () => {
    if (!imagePreview) {
      toast.error('Please select an image first');
      return;
    }
    
    setIsAnalyzingImage(true);
    setImageSearchResult(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/search/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          image: imagePreview,
          query: imageQuery || null,
          include_web_search: true,
          include_shopping: true
        })
      });
      
      if (!res.ok) {
        throw new Error('Image search failed');
      }
      
      const data = await res.json();
      setImageSearchResult(data);
      toast.success('Image analyzed successfully!');
      
    } catch (error) {
      toast.error('Failed to analyze image. Please try again.');
      console.error(error);
    } finally {
      setIsAnalyzingImage(false);
    }
  };
  
  const clearImageSearch = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageSearchResult(null);
    setImageQuery('');
    setShowImageSearch(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Save Search
  const handleSaveSearch = async () => {
    if (!searchResult) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/search/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query,
          result: { ...searchResult, answer: streamingAnswer }
        })
      });
      
      if (res.ok) {
        toast.success('Search saved!');
        fetchSavedSearches();
      }
    } catch (e) {
      toast.error('Failed to save search');
    }
  };
  
  // Share Search
  const handleShare = async () => {
    const url = `${window.location.origin}/search?q=${encodeURIComponent(query)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: `AIRA Search: ${query}`, url });
      } catch (e) { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };
  
  // Clear History
  const handleClearHistory = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/search/history`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchHistory([]);
      toast.success('History cleared');
    } catch (e) {
      toast.error('Failed to clear history');
    }
  };
  
  // New Search
  const handleNewSearch = () => {
    setQuery('');
    setSearchResult(null);
    setStreamingAnswer('');
    setConversationId(null);
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      {/* Magazine Background - Rotating Images */}
      <MagazineBackground />
      
      {/* Original Background Effects - now on top of magazine */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[1]">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/10 blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-cyan-600/10 blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-blue-600/5 blur-[200px]" />
      </div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                AIRA Search
              </h1>
              <p className="text-xs text-gray-500">AI-Powered Intelligence</p>
            </div>
          </motion.div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              data-testid="toggle-history"
            >
              <History className="w-5 h-5 text-gray-400" />
            </button>
            {(searchResult || streamingAnswer) && (
              <button
                onClick={handleNewSearch}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-sm"
                data-testid="new-search"
              >
                <Plus className="w-4 h-4" />
                New Search
              </button>
            )}
          </div>
        </header>
        
        {/* Search Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Search Mode Selector */}
          <div className="flex items-center gap-2 mb-4">
            {SEARCH_MODES.map(mode => {
              const Icon = mode.icon;
              const isActive = searchMode === mode.id;
              return (
                <button
                  key={mode.id}
                  onClick={() => setSearchMode(mode.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? `bg-gradient-to-r ${mode.gradient} text-white shadow-lg` 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid={`mode-${mode.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{mode.label}</span>
                  {isActive && <span className="text-xs opacity-70 hidden sm:inline">- {mode.desc}</span>}
                </button>
              );
            })}
          </div>
          
          {/* Main Search Bar */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-50" />
            <div className="relative flex items-center gap-3 p-2 bg-[#12141A]/80 backdrop-blur-xl border border-white/10 rounded-2xl">
              <div className="flex-1 flex items-center gap-3 px-4">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Ask anything... Search the web and AIRA"
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500 text-lg py-3"
                  disabled={isSearching}
                  data-testid="search-input"
                />
                {query && !isSearching && (
                  <button
                    onClick={() => setQuery('')}
                    className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              
              {/* Voice Button */}
              <button
                onClick={isListening ? stopVoiceSearch : startVoiceSearch}
                className={`p-3 rounded-xl transition-all duration-300 ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid="voice-search"
                title="Voice search"
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              
              {/* Image Search Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-3 rounded-xl bg-white/5 text-gray-400 hover:bg-white/10 hover:text-purple-400 transition-all duration-300"
                data-testid="image-search-button"
                title="Search with image"
              >
                <Camera className="w-5 h-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="image-input"
              />
              
              {/* Search Button */}
              <button
                onClick={() => handleSearch()}
                disabled={isSearching || !query.trim()}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                data-testid="search-button"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="hidden sm:inline">Searching</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span className="hidden sm:inline">Search</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Status Message */}
          {statusMessage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 flex items-center gap-2 text-sm text-gray-400"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              {statusMessage}
            </motion.div>
          )}
        </motion.div>
        
        {/* Results Section */}
        <AnimatePresence mode="wait">
          {(streamingAnswer || searchResult) && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* AI Answer Card */}
              <div className="relative p-6 rounded-2xl bg-[#12141A]/60 backdrop-blur-xl border border-white/10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-cyan-500/5" />
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-300">AIRA Answer</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveSearch}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        title="Save"
                        data-testid="save-search"
                      >
                        <Bookmark className="w-4 h-4 text-gray-400" />
                      </button>
                      <button
                        onClick={handleShare}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        title="Share"
                        data-testid="share-search"
                      >
                        <Share2 className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  
                  <div 
                    ref={answerRef}
                    className="prose prose-invert prose-sm max-w-none"
                    data-testid="ai-answer"
                  >
                    <div className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                      {streamingAnswer}
                      {isSearching && <span className="inline-block w-2 h-5 bg-purple-400 animate-pulse ml-1" />}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sources */}
              {searchResult?.sources?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Sources ({searchResult.sources.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {searchResult.sources.map((source, i) => (
                      <SourceCard key={source.url || `source-${i}`} source={source} index={i} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Related Questions */}
              {searchResult?.related_questions?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Related Questions
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {searchResult.related_questions.map((q, i) => (
                      <button
                        key={q}
                        onClick={() => {
                          setQuery(q);
                          handleSearch(q, true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-gray-300 hover:text-white transition-all"
                        data-testid={`related-question-${i}`}
                      >
                        {q}
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Follow-up Input */}
              {conversationId && !isSearching && (
                <div className="flex items-center gap-3 p-2 bg-[#12141A]/60 backdrop-blur-xl border border-white/10 rounded-xl">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(query, true)}
                    placeholder="Ask a follow-up question..."
                    className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500 px-4 py-2"
                    data-testid="follow-up-input"
                  />
                  <button
                    onClick={() => handleSearch(query, true)}
                    disabled={!query.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg text-white font-medium disabled:opacity-50"
                    data-testid="follow-up-button"
                  >
                    Ask
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Initial State - Trending & History */}
        {!streamingAnswer && !searchResult && !isSearching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Trending Searches */}
            {trendingSearches.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Trending Searches
                </h3>
                <div className="flex flex-wrap gap-2">
                  {trendingSearches.map((item) => (
                    <button
                      key={item.query}
                      onClick={() => {
                        setQuery(item.query);
                        handleSearch(item.query);
                      }}
                      className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-gray-300 hover:text-white transition-all"
                      data-testid={`trending-${item.query.slice(0, 10)}`}
                    >
                      {item.query}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Quick Suggestions */}
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-4">Try asking about...</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: Globe, text: 'Latest news and world events', query: 'What are the top news stories today?' },
                  { icon: Brain, text: 'AI and technology trends', query: 'What are the latest AI breakthroughs?' },
                  { icon: Users, text: 'People and culture', query: 'Who are the most influential people in tech?' },
                  { icon: ImageIcon, text: 'Places and travel', query: 'Best destinations to visit in 2026' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.query}
                      onClick={() => {
                        setQuery(item.query);
                        handleSearch(item.query);
                      }}
                      className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-all group"
                      data-testid={`suggestion-${item.text.slice(0, 10)}`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-cyan-500/30 transition-all">
                        <Icon className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{item.text}</span>
                      <ChevronRight className="w-4 h-4 text-gray-500 ml-auto group-hover:text-purple-400 transition-colors" />
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
        
        {/* History Sidebar */}
        <AnimatePresence>
          {showHistory && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                onClick={() => setShowHistory(false)}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#12141A] border-l border-white/10 z-50 overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Search History</h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleClearHistory}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-red-400"
                        title="Clear history"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowHistory(false)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Recent Searches */}
                  <div className="mb-8">
                    <h3 className="text-sm text-gray-400 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Recent
                    </h3>
                    <div className="space-y-2">
                      {searchHistory.length === 0 ? (
                        <p className="text-sm text-gray-500">No recent searches</p>
                      ) : (
                        searchHistory.map((item) => (
                          <button
                            key={item.query}
                            onClick={() => {
                              setQuery(item.query);
                              handleSearch(item.query);
                              setShowHistory(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 text-left transition-colors"
                          >
                            <Search className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-300 truncate">{item.query}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Saved Searches */}
                  <div>
                    <h3 className="text-sm text-gray-400 mb-3 flex items-center gap-2">
                      <Bookmark className="w-4 h-4" />
                      Saved
                    </h3>
                    <div className="space-y-2">
                      {savedSearches.length === 0 ? (
                        <p className="text-sm text-gray-500">No saved searches</p>
                      ) : (
                        savedSearches.map((item) => (
                          <button
                            key={item.query}
                            onClick={() => {
                              setQuery(item.query);
                              setSearchResult(item.result);
                              setStreamingAnswer(item.result?.answer || item.result?.ai_answer?.content || '');
                              setShowHistory(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 text-left transition-colors"
                          >
                            <Bookmark className="w-4 h-4 text-purple-400" />
                            <span className="text-sm text-gray-300 truncate">{item.query}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        
        {/* Image Search Modal */}
        <AnimatePresence>
          {showImageSearch && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
                onClick={clearImageSearch}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#12141A] border border-white/10 rounded-2xl z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-white">Image Search</h2>
                        <p className="text-xs text-gray-500">Search with any image</p>
                      </div>
                    </div>
                    <button
                      onClick={clearImageSearch}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                  
                  {/* Image Preview or Drop Zone */}
                  {imagePreview ? (
                    <div className="relative mb-6">
                      <img
                        src={imagePreview}
                        alt="Selected"
                        className="w-full max-h-64 object-contain rounded-xl bg-white/5"
                      />
                      <button
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                          setImageSearchResult(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-white/20 rounded-2xl p-8 text-center mb-6 hover:border-purple-500/50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleImageDrop}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-400 mb-2">Drag and drop an image here</p>
                      <p className="text-sm text-gray-500">or click to browse</p>
                    </div>
                  )}
                  
                  {/* Optional Question */}
                  <div className="mb-6">
                    <label className="text-sm text-gray-400 mb-2 block">Ask about this image (optional)</label>
                    <input
                      type="text"
                      value={imageQuery}
                      onChange={(e) => setImageQuery(e.target.value)}
                      placeholder="e.g., What brand is this? Where can I buy this?"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-purple-500/50"
                    />
                  </div>
                  
                  {/* Analyze Button */}
                  <button
                    onClick={handleImageSearch}
                    disabled={!imagePreview || isAnalyzingImage}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    data-testid="analyze-image-button"
                  >
                    {isAnalyzingImage ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        Analyze Image
                      </>
                    )}
                  </button>
                  
                  {/* Results */}
                  {imageSearchResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 space-y-6"
                    >
                      {/* Analysis */}
                      {imageSearchResult.image_analysis && (
                        <div className="p-4 bg-white/5 rounded-xl">
                          <h3 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            AI Analysis
                          </h3>
                          
                          {/* Answer (if query was provided) */}
                          {imageSearchResult.image_analysis.answer && (
                            <div className="mb-4 p-3 bg-purple-500/10 rounded-lg">
                              <p className="text-white">{imageSearchResult.image_analysis.answer}</p>
                            </div>
                          )}
                          
                          {/* Description */}
                          <p className="text-gray-300 text-sm mb-4">
                            {imageSearchResult.image_analysis.description}
                          </p>
                          
                          {/* Objects/Tags */}
                          {imageSearchResult.image_analysis.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {imageSearchResult.image_analysis.tags.map((tag, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 bg-white/10 text-gray-300 text-xs rounded-lg"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Text Found */}
                          {imageSearchResult.image_analysis.text_found && (
                            <div className="p-3 bg-white/5 rounded-lg mb-4">
                              <span className="text-xs text-gray-500">Text detected:</span>
                              <p className="text-gray-300 text-sm mt-1">{imageSearchResult.image_analysis.text_found}</p>
                            </div>
                          )}
                          
                          {/* Product Info */}
                          {imageSearchResult.image_analysis.product_info && (
                            <div className="p-3 bg-orange-500/10 rounded-lg mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Tag className="w-4 h-4 text-orange-400" />
                                <span className="text-sm font-medium text-orange-400">Product Detected</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {imageSearchResult.image_analysis.product_info.brand && (
                                  <div>
                                    <span className="text-gray-500">Brand: </span>
                                    <span className="text-white">{imageSearchResult.image_analysis.product_info.brand}</span>
                                  </div>
                                )}
                                {imageSearchResult.image_analysis.product_info.category && (
                                  <div>
                                    <span className="text-gray-500">Category: </span>
                                    <span className="text-white">{imageSearchResult.image_analysis.product_info.category}</span>
                                  </div>
                                )}
                                {imageSearchResult.image_analysis.product_info.price_range && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500">Est. Price: </span>
                                    <span className="text-white">{imageSearchResult.image_analysis.product_info.price_range}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Suggested Searches */}
                      {imageSearchResult.suggested_searches?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-400 mb-3">Suggested Searches</h3>
                          <div className="flex flex-wrap gap-2">
                            {imageSearchResult.suggested_searches.map((search, i) => (
                              <button
                                key={i}
                                onClick={() => {
                                  setQuery(search);
                                  clearImageSearch();
                                  handleSearch(search);
                                }}
                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 text-sm rounded-lg flex items-center gap-1 transition-colors"
                              >
                                <Search className="w-3 h-3" />
                                {search}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Shop This Look */}
                      {imageSearchResult.shopping_results && (
                        <div className="p-4 bg-orange-500/10 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ShoppingBag className="w-5 h-5 text-orange-400" />
                              <span className="font-medium text-white">Shop Similar Products</span>
                            </div>
                            <button
                              onClick={() => {
                                clearImageSearch();
                                navigate(imageSearchResult.shopping_results.search_url);
                              }}
                              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg flex items-center gap-1 transition-colors"
                            >
                              Shop Now <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Web Results */}
                      {imageSearchResult.web_results?.sources?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Related Web Results
                          </h3>
                          <div className="space-y-2">
                            {imageSearchResult.web_results.sources.slice(0, 3).map((source, i) => (
                              <a
                                key={i}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-gray-500">{source.domain}</span>
                                </div>
                                <p className="text-sm text-white line-clamp-1">{source.title}</p>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
